<template>
  <div class="space-y-8">
    <!-- Progress Steps -->
    <div
      v-if="!hideProgressSteps"
      class="flex items-center justify-center mb-8 border-2 py-12 border-black rounded-2xl p-6 max-w-3xl mx-auto bg-white shadow-[6px_6px_0px_#000]"
    >
      <div class="flex items-center space-x-4">
        <!-- Step 1 -->
        <div class="flex items-center">
          <div class="step-indicator active">
            <span class="text-white font-semibold">1</span>
          </div>
          <span class="ml-2 text-sm font-medium text-gray-900">Select Date & Time</span>
        </div>

        <!-- Connector -->
        <div class="w-16 h-1 bg-gray-200 rounded"></div>

        <!-- Step 2 -->
        <div class="flex items-center">
          <div class="step-indicator inactive">
            <span class="text-gray-500 font-semibold">2</span>
          </div>
          <span class="ml-2 text-sm font-medium text-gray-500">Session Details</span>
        </div>
      </div>
    </div>

    <!-- Calendar Card -->
    <div
      class="border-2 border-black py-6 px-10 rounded-3xl bg-white shadow-[6px_6px_0px_#000] max-w-5xl mx-auto overflow-hidden"
    >
      <!-- Header Section -->
      <div class="text-center pt-8 pb-6 px-8 bg-white">
        <h2 class="text-xl font-bold text-gray-900 mb-2">Select Date & Time</h2>
        <p class="text-gray-600">
          Choose your preferred session time from {{ tutorName }}'s available slots
        </p>
      </div>

      <!-- Available Times Section -->
      <div class="py-4">
        <div class="flex items-center justify-between">
          <div>
            <h4 class="text-lg font-semibold text-gray-900">Available Times</h4>
            <p class="text-sm text-gray-500">Timezone: Philippines (GMT+8)</p>
            <div v-if="loadingError" class="mt-2">
              <div class="text-sm text-red-600 mb-2">{{ loadingError }}</div>
              <button
                @click="loadAvailableSlots"
                class="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
              >
                Retry Loading
              </button>
            </div>
          </div>

          <!-- Month Navigation -->
          <div class="flex items-center space-x-4">
            <button
              @click="previousWeek"
              class="arrow-button w-6 h-6 flex items-center justify-center border border-gray-300 rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <ChevronLeftIcon class="w-3 h-3" />
            </button>

            <span class="text-lg font-semibold text-gray-900 w-40 text-center">{{
              currentMonthYear
            }}</span>

            <button
              @click="nextWeek"
              :disabled="isNextWeekDisabled"
              class="arrow-button w-6 h-6 flex items-center justify-center border rounded-md transition-colors"
              :class="
                isNextWeekDisabled
                  ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-50'
                  : 'border-gray-300 hover:bg-gray-100 cursor-pointer'
              "
            >
              <ChevronRightIcon class="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      <!-- Calendar Grid -->
      <div
        class="bg-white border-2 mb-4 border-black rounded-xl shadow-[6px_6px_0px_#000] overflow-hidden"
      >
        <!-- Days Header with Dates -->
        <div class="grid grid-cols-7 border-b-2 border-black">
          <div
            v-for="(day, index) in weekDays"
            :key="`header-${day.date}-${day.month}`"
            class="text-center py-2 sm:py-4 px-1 sm:px-2 border-r border-gray-200 last:border-r-0"
            :class="{
              'bg-blue-200 rounded-tl-xl': day.isToday && index === 0,
              'bg-blue-200 rounded-tr-xl': day.isToday && index === 6,
              'bg-blue-200': day.isToday && index !== 0 && index !== 6,
              'bg-blue-100 rounded-tl-xl': !day.isToday && index === 0,
              'bg-blue-100 rounded-tr-xl': !day.isToday && index === 6,
              'bg-blue-100': !day.isToday && index !== 0 && index !== 6,
            }"
          >
            <div class="text-xs font-medium text-gray-600 uppercase">{{ dayHeaders[index] }}</div>
            <div
              class="text-sm sm:text-lg font-bold mt-1"
              :class="{
                'text-gray-400': day.isPast,
                'text-gray-900': !day.isPast,
              }"
            >
              {{ day.date }}
            </div>
          </div>
        </div>

        <!-- Time Slots Grid -->
        <div class="grid grid-cols-7">
          <div
            v-for="(day, index) in weekDays"
            :key="`slots-${day.date}-${day.month}`"
            class="min-h-[120px] sm:min-h-[180px] p-1 sm:p-2 border-r border-gray-200 last:border-r-0 relative"
            :class="{
              'bg-gray-50 rounded-bl-3xl':
                (day.isPast || day.timeSlots.length === 0) && index === 0,
              'bg-gray-50 rounded-br-3xl':
                (day.isPast || day.timeSlots.length === 0) && index === 6,
              'bg-gray-50':
                (day.isPast || day.timeSlots.length === 0) && index !== 0 && index !== 6,
              'bg-white rounded-bl-3xl': !day.isPast && day.timeSlots.length > 0 && index === 0,
              'bg-white rounded-br-3xl': !day.isPast && day.timeSlots.length > 0 && index === 6,
              'bg-white': !day.isPast && day.timeSlots.length > 0 && index !== 0 && index !== 6,
            }"
          >
            <!-- Past Date or No Slots Message -->
            <div
              v-if="day.isPast"
              class="flex flex-col items-center justify-center h-full text-gray-400"
            >
              <div class="flex flex-col items-center gap-1 text-sm font-medium">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  ></path>
                </svg>
                <span class="text-xs text-center">Past Date</span>
              </div>
            </div>

            <div
              v-else-if="day.timeSlots.length === 0"
              class="flex flex-col items-center justify-center h-full text-gray-400"
            >
              <div class="text-xs text-center px-1">No available slots</div>
            </div>

            <!-- Loading State -->
            <div
              v-else-if="isLoadingSlots"
              class="flex flex-col items-center justify-center h-full text-gray-400"
            >
              <div class="animate-pulse">
                <div class="space-y-2">
                  <div class="h-8 bg-gray-200 rounded w-full"></div>
                  <div class="h-8 bg-gray-200 rounded w-full"></div>
                  <div class="h-8 bg-gray-200 rounded w-full"></div>
                </div>
              </div>
            </div>

            <!-- Error State -->
            <div
              v-else-if="loadingError"
              class="flex flex-col items-center justify-center h-full text-red-500"
            >
              <div class="text-center">
                <div class="text-sm font-medium mb-1">Error Loading Slots</div>
                <div class="text-xs">{{ loadingError }}</div>
                <button
                  @click="loadAvailableSlots"
                  class="mt-2 px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>

            <!-- Available Time Slots -->
            <div v-else class="space-y-1 sm:space-y-2">
              <button
                v-for="slot in day.timeSlots"
                :key="slot.time"
                @click="selectTimeSlot(day, slot)"
                class="w-full text-xs sm:text-sm py-1.5 sm:py-2 px-0.5 sm:px-1 rounded-md transition-all duration-200 font-medium overflow-hidden text-ellipsis cursor-pointer"
                :class="getTimeSlotClass(day, slot)"
                :disabled="!slot.available"
                :title="
                  slot.isAvailabilitySlot
                    ? `${slot.fullTimeRange} (${slot.durationText}, ${slot.mode})`
                    : slot.time
                "
              >
                <span class="block truncate">{{ slot.time }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Session Type Legend -->
      <div class="flex justify-center">
        <div class="flex items-center gap-6 text-xs text-gray-600 px-4 py-2">
          <div class="flex items-center gap-1">
            <div class="w-3 h-3 rounded bg-blue-100 border border-blue-200"></div>
            <span>Online</span>
          </div>
          <div class="flex items-center gap-1">
            <div class="w-3 h-3 rounded bg-orange-100 border border-orange-200"></div>
            <span>Face-to-Face</span>
          </div>
          <div class="flex items-center gap-1">
            <div class="w-3 h-3 rounded bg-green-100 border border-green-200"></div>
            <span>Both (Online or Face-to-face)</span>
          </div>
        </div>
      </div>

      <!-- Selected Time Display -->
      <div v-if="selectedDateTime" class="mt-4 p-4 bg-white border border-gray-200 rounded-lg">
        <div class="flex items-start">
          <!-- Date Section -->
          <div class="flex-1 pr-4">
            <div class="mb-2">
              <p class="text-xs text-gray-500 font-medium">Selected Date</p>
            </div>
            <div class="flex items-center gap-2">
              <svg
                class="w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                ></path>
              </svg>
              <p class="text-sm font-semibold text-gray-900">
                {{
                  new Date(
                    selectedDateTime.date.year,
                    selectedDateTime.date.month,
                    selectedDateTime.date.date
                  ).toLocaleDateString("en-US", {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })
                }}
              </p>
            </div>
          </div>

          <!-- Vertical Divider -->
          <div class="w-px h-12 bg-gray-200 mx-4"></div>

          <!-- Time Section -->
          <div class="flex-1 pl-4">
            <div class="mb-2">
              <p class="text-xs text-gray-500 font-medium">Selected Time</p>
            </div>
            <div class="flex items-center gap-2">
              <svg
                class="w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
              <p class="text-sm font-semibold text-gray-900">
                {{ selectedDateTime.timeSlot.fullTimeRange || selectedDateTime.timeSlot.time }}
              </p>
              <span
                class="ml-2 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full"
              >
                {{ selectedDateTime.timeSlot.durationText || "1h" }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex justify-end pt-6 pb-2">
        <button
          type="button"
          @click="handleNext"
          class="next-button flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded-lg border border-blue-800 shadow-[4px_4px_0px_#193CB8] transition-colors font-medium cursor-pointer"
          :disabled="!selectedDateTime"
        >
          Next
          <ChevronRightIcon class="w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, onUnmounted } from "vue";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/24/outline";
import { availabilityService } from "@/services/availabilityService";
// import type { TimeSlot, SessionDateTime } from '@/types/availability'
import {
  getStartOfWeek,
  isToday,
  isPastDate,
  formatDateToString,
  addWeeksToDate,
  subtractWeeksFromDate,
  getWeekDays,
  formatDateForDisplay,
  formatTimeForDisplay,
} from "@/utils/dateUtils";

const props = defineProps({
  tutor: {
    type: Object,
    default: null,
  },
  currentStep: {
    type: Number,
    default: 1,
  },
  hideProgressSteps: {
    type: Boolean,
    default: false,
  },
  selectedDateTime: {
    type: Object,
    default: null,
  },
  selectedWeekDate: {
    type: Date,
    default: null,
  },
});

const emit = defineEmits(["next-step", "datetime-selected"]);

const currentDate = ref(new Date());
const selectedDateTime = ref(null);
const availableSlots = ref({});
const isLoadingSlots = ref(true); // Start as loading to prevent flash of empty slots
const loadingError = ref(null);
const weekDaysData = ref([]);

const dayHeaders = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

const tutorName = computed(() => props.tutor?.name || "Sarah Chen");

const currentWeekRange = computed(() => {
  const startOfWeek = getStartOfWeek(currentDate.value);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);

  const options = { month: "long", day: "numeric", year: "numeric" };
  const startStr = startOfWeek.toLocaleDateString("en-US", options);
  const endStr = endOfWeek.toLocaleDateString("en-US", options);

  return `${startStr} - ${endStr}`;
});

const currentMonthYear = computed(() => {
  const startOfWeek = getStartOfWeek(currentDate.value);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);

  const startMonth = startOfWeek.toLocaleDateString("en-US", { month: "short" });
  const endMonth = endOfWeek.toLocaleDateString("en-US", { month: "short" });
  const year = startOfWeek.getFullYear();

  // If the week spans two different months, show "Sep - Oct 2024" format
  if (startOfWeek.getMonth() !== endOfWeek.getMonth()) {
    return `${startMonth} - ${endMonth} ${year}`;
  }

  // If the week is within the same month, show the month and year
  return currentDate.value.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
});

// Using the utility function from dateUtils

const formatSelectedDateTime = computed(() => {
  if (!selectedDateTime.value) return "";

  const { date, timeSlot } = selectedDateTime.value;
  const dateStr = new Date(date.year, date.month, date.date).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  // Show full details for availability slots
  if (timeSlot.isAvailabilitySlot) {
    return `${dateStr} from ${timeSlot.fullTimeRange}`;
  }

  return `${dateStr} at ${timeSlot.time}`;
});

const weekDays = computed(() => {
  const startOfWeek = getStartOfWeek(currentDate.value);
  const days = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Generate 7 days for the current week using date-fns
  const weekDates = getWeekDays(startOfWeek);

  for (let i = 0; i < 7; i++) {
    const date = weekDates[i];
    date.setHours(0, 0, 0, 0); // Reset time to compare only dates

    const dayKey = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    const timeSlots = availableSlots.value[dayKey] || [];

    // Construct a real Date object for isPastDate
    const realDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const dayData = {
      date: date.getDate(),
      month: date.getMonth(),
      year: date.getFullYear(),
      // Only mark as past if the date is before today (not if it's today at midnight)
      isPast: isPastDate(realDate) && !isToday(realDate),
      isToday: isToday(realDate),
      timeSlots: timeSlots,
    };

    days.push(dayData);
  }

  return days;
});

const getTimeSlotClass = (day, slot) => {
  if (!slot.available) {
    return "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200";
  }

  const isSelected =
    selectedDateTime.value &&
    selectedDateTime.value.date.date === day.date &&
    selectedDateTime.value.date.month === day.month &&
    selectedDateTime.value.date.year === day.year &&
    selectedDateTime.value.timeSlot.time === slot.time;

  // Color code based on session mode
  const mode = slot.mode?.toLowerCase();

  if (isSelected) {
    // Selected state with color coding
    if (mode === "online") {
      return "bg-blue-600 text-white border border-blue-600";
    } else if (mode === "face-to-face" || mode === "in-person") {
      return "bg-orange-600 text-white border border-orange-600";
    } else if (mode === "both") {
      return "bg-green-600 text-white border border-green-600";
    }
    // Default selected state
    return "bg-green-600 text-white border border-green-600";
  }

  // Unselected state with color coding
  if (mode === "online") {
    return "bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 hover:border-blue-300 transition-colors";
  } else if (mode === "face-to-face" || mode === "in-person") {
    return "bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100 hover:border-orange-300 transition-colors";
  } else if (mode === "both") {
    return "bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 hover:border-green-300 transition-colors";
  }

  // Default fallback - green for available slots
  return "bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 hover:border-green-300 transition-colors";
};

const selectTimeSlot = (day, slot) => {
  if (!slot.available) return;

  selectedDateTime.value = {
    date: day,
    timeSlot: slot,
  };
};

const clearSelection = () => {
  selectedDateTime.value = null;
};

const previousWeek = () => {
  currentDate.value = subtractWeeksFromDate(currentDate.value, 1);
  loadAvailableSlots();
};

const isNextWeekDisabled = computed(() => {
  return false; // Allow unlimited forward navigation
});

const nextWeek = () => {
  currentDate.value = addWeeksToDate(currentDate.value, 1);
  loadAvailableSlots();
};

const loadAvailableSlots = async () => {
  isLoadingSlots.value = true;
  loadingError.value = null;

  try {
    // Load from tutor's Firebase availability
    if (props.tutor?.id) {
      const startOfWeek = getStartOfWeek(currentDate.value);
      const slots = await availabilityService.generateWeeklySlots(props.tutor.id, startOfWeek);
      availableSlots.value = slots;
    } else {
      // No tutor specified - use empty slots
      availableSlots.value = {};
    }
  } catch (error) {
    loadingError.value = "Failed to load available time slots. Please try again.";
    // Fallback to empty slots on error
    availableSlots.value = {};
  } finally {
    isLoadingSlots.value = false;
  }
};

const handleNext = () => {
  if (selectedDateTime.value) {
    const dateTimeData = {
      date: selectedDateTime.value.date,
      timeSlot: selectedDateTime.value.timeSlot,
      tutorId: props.tutor?.id,
      weekDate: currentDate.value, // Include the current week date
    };
    emit("datetime-selected", dateTimeData);
    emit("next-step");
  }
};

// Watch for changes in currentDate to reload slots
watch(currentDate, () => {
  loadAvailableSlots();
});

// Watch for changes in selectedDateTime prop to restore selection
watch(
  () => props.selectedDateTime,
  (newSelection) => {
    if (newSelection) {
      selectedDateTime.value = newSelection;
    }
  },
  { immediate: true }
);

// Watch for changes in selectedWeekDate prop to restore week view
watch(
  () => props.selectedWeekDate,
  (newWeekDate) => {
    if (newWeekDate) {
      currentDate.value = new Date(newWeekDate);
    }
  },
  { immediate: true }
);

onMounted(() => {
  loadAvailableSlots();

  // Listen for availability changes and refresh slots automatically
  const handleAvailabilityChange = (data) => {
    if (!data.tutorId || data.tutorId === props.tutor?.id) {
      // Refresh slots if it's for our tutor or if tutorId is not specified
      loadAvailableSlots();
    }
  };

  availabilityService.onAvailabilityChanged(handleAvailabilityChange);

  // Set up periodic refresh to ensure data stays current (every 3 minutes)
  // Reduced frequency since we have real-time listeners for most changes
  const refreshInterval = setInterval(() => {
    loadAvailableSlots();
  }, 180000); // 3 minutes instead of 30 seconds

  // Clean up listener and interval on unmount
  onUnmounted(() => {
    availabilityService.offAvailabilityChanged(handleAvailabilityChange);
    clearInterval(refreshInterval);
  });
});
</script>

<style scoped>
.arrow-button {
  border: 2px solid #000000;
  border-radius: 20%;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 2px 2px 0px #000000;
}
.step-indicator {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.step-indicator.active {
  background-color: #3b82f6;
  color: white;
}

.step-indicator.inactive {
  background-color: #f3f4f6;
  color: #6b7280;
}

button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.next-button:enabled:hover {
  background-color: #1d4ed8;
}
</style>
